<template>
  <div class="attributes">
    <div class="split-line" />
    <div class="select-variants">
      Select Variants and Quantity
    </div>
    <div class="sku-attribute-title">
      <span>Variants ({{ skuImages[0]?.attributeNameTrans }}): </span>
      <span class="desc">{{ hoverSku ? hoverSku.valueTrans : checkedSku?.valueTrans }}</span>
    </div>
    <div class="sku-images">
      <div
        v-for="(item, index) in getSkuImages"
        :key="index"
        @mouseenter="hoverSku = item"
        @mouseleave="hoverSku = null"
        @click="handleCheckSku(item, index)"
        class="sku-images-item"
      >
        <div
          :class="{ checked: activeIndex === index }"
          class="outline"
        >
          <div class="image-wrapper">
            <Tooltip
              :content="item.valueTrans"
              :offset="8"
            >
              <Image
                v-if="item.skuImageUrl"
                :src="item.skuImageUrl"
                class="image"
              />
              <img
                v-else
                class="image"
                src="@/assets/empty-image.svg"
              >
            </Tooltip>
            <div
              v-if="item.quantity"
              class="quantity"
            >
              <span class="prefix">x</span>
              {{ item.quantity }}
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="skuImages.length > 14"
        @click="showAll = !showAll"
        class="view-all"
      >
        <span class="text">
          <template v-if="!showAll">
            View <br>
            all {{ skuImages.length }}
          </template>
          <template v-else>
            View <br>
            less
          </template>
        </span>
      </div>
    </div>
    <div class="sku-attributes">
      <div class="sku-attributes-header">
        <div class="header-left">
          {{ skuImages[activeIndex]?.singleAttr ? 'Quantity:' : currentAttributesList[0]?.attributeNameTrans }}
          <template v-if="!skuImages[activeIndex]?.singleAttr">
            ({{ currentAttributesList.length }}):
          </template>
          <span class="min-order"> Min.Order: {{ detailInfo?.minOrderQuantity }} PCS</span>
        </div>
        <div
          @click="clearAttributes"
          class="header-right"
        >
          Clear all
        </div>
      </div>
      <div
        :class="{ 'scroll-bar': currentAttributesList.length > 5 }"
        class="sku-attributes-list"
      >
        <div
          v-for="(item, index) in currentAttributesList"
          :key="index"
          class="sku-attributes-list-item"
        >
          <template v-if="item.singleAttr">
            <div class="single-attirbute">
              <NumberInput
                class="number-input"
                v-model="item.quantity"
                :disabled-minus="item.quantity <= 0"
                :disabled-plus="item.amountOnSale === 0 || item.quantity >= item.amountOnSale"
                :min="0"
                :max="item.amountOnSale"
                :step="item.bundleQuantity"
                @change="handleNumberInputChange(item)"
              />
              <div class="stock-count">
                {{ item.amountOnSale > 999 ? '999+' : item.amountOnSale }} in stock
              </div>
            </div>
          </template>
          <template v-else>
            <div
              :title="item.valueTrans"
              :class="{ checked: item.checked }"
              class="item-left"
            >
              {{ item.valueTrans }}
            </div>
            <div class="item-right">
              <div class="stock-count">
                {{ item.amountOnSale > 999 ? '999+' : item.amountOnSale }} in stock
              </div>
              <NumberInput
                class="number-input"
                v-model="item.quantity"
                :disabled-minus="item.quantity <= 0"
                :disabled-plus="item.amountOnSale === 0 || item.quantity >= item.amountOnSale"
                :min="0"
                :max="item.amountOnSale"
                :step="item.bundleQuantity"
                @change="handleNumberInputChange(item)"
              />
            </div>
          </template>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { debounce } from '@/utils/utils'
import { Image } from '@/components/Image'
import NumberInput from '@/components/NumberInput/index.vue'
import { Tooltip } from '@/components/Tooltip'
import { useProductDetail } from '@/views/OfferView/components/hooks/useProductDetail'

const props = defineProps({
  detailInfo: {
    type: Object,
    default: () => ({})
  }
})

const emits = defineEmits(['checkSku'])

const { skuAttributes, formatImageList } = useProductDetail()

const skuImages = ref([]) // sku数目

const checkedSku = ref()

const hoverSku = ref(null)

const activeIndex = ref(0)

const showAll = ref(false)

const currentAttributesList = computed(() => {
  if (!checkedSku.value) return []

  const valueTrans = checkedSku.value.valueTrans
  return skuAttributes.value.filter(item => item.tag === valueTrans)
})

const getSkuImages = computed(() => {
  const len = skuImages.value.length
  if (len <= 14) return skuImages.value

  return showAll.value ? skuImages.value : skuImages.value.slice(0, 13)
})

// 初始化 skuImages，skuAttributes
const initData = (value) => {
  skuImages.value = formatImageList(value)

  handleDefaultAction()
}

const handleCheckSku = (item, index) => {
  checkedSku.value = item
  activeIndex.value = index
  emits('checkSku', item, index)
}

// 默认选中以及默认加购
const handleDefaultAction = () => {
  // 选中价格最低的sku
  let index = 0
  let firstPrice = skuImages.value[0].price
  skuImages.value.forEach((item, _index) => {
    if (Number(item.price) < Number(firstPrice)) {
      firstPrice = item.price
      index = _index
    }
  })
  if (index > 8) {
    showAll.value = true
  }

  handleCheckSku(skuImages.value[index], index)

  // 找到当前sku对应的规格
  const valueTrans = checkedSku.value.valueTrans
  const attributes = skuAttributes.value.filter(item => item.tag === valueTrans)

  // 设置第二个区间价的数量
  const priceRangeList = checkedSku.value.priceRangeList
  const quantity = Math.max((priceRangeList[1] ?? priceRangeList[0]).startQuantity, checkedSku.value.bundleQuantity)
  checkedSku.value.quantity = quantity
  attributes[0].quantity = quantity
  attributes[0].checked = true
}

const handleQuantityChange = debounce((item) => {
  setImagesQuantity(item)
  handleCheckStatus(item)
}, 200)

const handleNumberInputChange = (item) => {
  let val = calcBundleQuantity(item)

  if (val > item.amountOnSale) {
    val = item.amountOnSale
  }
  item.quantity = val

  handleQuantityChange(item)
}

const handleCheckStatus = (item) => {
  skuAttributes.value.forEach(v => {
    v.checked = false
  })
  if (item.quantity > 0) {
    item.checked = true
  }
}

/**
 * 捆绑数量算法， 与 购物车 逻辑存在差异
 * 1. 当数量不为 0 时，计算后的数量最小为 bundleQuantity
 */
const calcBundleQuantity = (data) => {
  const bundleQuantity = +data.bundleQuantity
  const halfBundle = bundleQuantity / 2
  let quantity = +data.quantity
  if (quantity > 0) {
    quantity = (Math.floor((quantity + halfBundle) / bundleQuantity)) * bundleQuantity || bundleQuantity
  }
  return quantity
}

const setImagesQuantity = (item) => {
  const quantity = currentAttributesList.value.reduce((pre, cur) => {
    return pre + cur.quantity
  }, 0)

  skuImages.value.forEach(v => {
    if (v.valueTrans === item.tag) {
      v.quantity = quantity
    }
  })
}

const clearAttributes = () => {
  skuAttributes.value.forEach(item => {
    item.quantity = 0
    item.checked = false
  })
  skuImages.value.forEach(item => {
    item.quantity = 0
  })
  activeIndex.value = null
}

onMounted(() => {
  initData(props.detailInfo)
})

defineExpose({
  clearAttributes
})

</script>

<style lang='less' scoped>
.attributes {
  display: flex;
  flex-direction: column;
  .split-line {
    margin-top: 16px;
    height: 1px;
    background-color: #EBF1F5;
  }

  .select-variants {
    margin: 20px 0 16px 0;
    color: #232323;
    font-size: 14px;
    font-family: mon-700;
  }

  .sku-attribute-title {
    color: #232323;
    font-size: 14px;
    font-family: mon-700;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
    line-height: 18px;
    .desc {
      font-size: 12px;
      font-family: mon-500;
    }
  }

  .sku-images {
    margin-top: 12px;
    display: flex;
    flex-wrap: wrap;
    gap: 2px;
    padding: 1px 0;

    .view-all {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 50px;
      .text {
        color: #5c5c5c;
        font-size: 11px;
        font-family: mon-500;
        text-decoration: underline;
        line-height: 14px;
        cursor: pointer;
        text-align: center;

        &:hover {
          color: var(--link-hover-color);
        }
      }
    }

    .outline {
      display: flex;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
      width: 50px;
      height: 50px;
      border-radius: 50%;
      cursor: pointer;

      &:hover {
        border: 1px solid #052784;
      }

      &.checked {
        border: 2px solid #052784;
        .c-image {
          border: 1px solid #EBF1F5
        }
      }

      .image-wrapper {
        position: relative;
        width: 44px;
        height: 44px;
        padding: 1px;
        border: 1px solid #f5f5f5;
        border-radius: 50%;

        .image {
          width: 40px;
          height: 40px;
          border-radius: 50%;
        }

        .quantity {
          position: absolute;
          top: -4px;
          right: -4px;
          height: 16px;
          padding: 0 6px;
          background-color: #009DE0;
          border-radius: 8px;
          color: white;
          font-size: 12px;
          font-family: mon-500;
          display: flex;
          align-items: center;
          outline: 2px solid white;
          .prefix {
            font-size: 10px;
          }
        }
      }
    }
  }

  .sku-attributes {
    margin-top: 16px;

    .sku-attributes-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin-bottom: 14px;
      .header-left {
        color: #232323;
        font-size: 14px;
        font-family: mon-700;
        .min-order {
          font-size: 11px;
          font-family: mon-500;
        }
      }
      .header-right {
        position: relative;
        height: 14px;
        color: #6E717A;
        font-size: 11px;
        font-family: mon-500;
        text-decoration: underline;
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        padding-right: 12px;

        &:hover {
          color: var(--link-hover-color);
        }

        &::before {
          content: '';
          display: block;
          position: relative;
          top: 1px;
          left: -8px;
          height: 8px;
          width: 1px;
          background-color: #DFDFDF;
        }
      }
    }

    .sku-attributes-list {
      max-height: 240px;
      overflow-y: scroll;
      padding-right: 8px;

      &::-webkit-scrollbar {
        /* 隐藏默认的滚动条 */
        -webkit-appearance: none;
      }

      &.scroll-bar {
        &::-webkit-scrollbar{
        width: 8px;
        }
        &::-webkit-scrollbar-thumb{
          border-radius: 1em;
          background-color: #EBEBEB;
        }

        &::-webkit-scrollbar-track{
          border-radius: 1em;
          background-color: #F8F8F8;
        }
      }
    }
    .sku-attributes-list-item {
      display: flex;
      align-items: center;
      justify-content: space-between;
      height: 32px;
      margin-bottom: 18px;
      padding-left: 4px;

      .item-left {
        height: 24px;
        padding: 4px 12px;
        color: #232323;
        font-size: 12px;
        font-family: mon-600;
        border-radius: 4px;
        background-color: #F8F8F8;
        max-width: 160px;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        &.checked {
          outline: 2px solid #052784;
          outline-offset: 2px;
        }
      }
      .item-right {
        display: flex;
        align-items: center;
        gap: 12px;
        .stock-count {
          color: #B2B7C2;
          font-size: 11px;
          font-family: mon-500;
        }
        .number-input {
          width: 96px;
          height: 32px;
          ::v-deep(.round-button) {
            width: 32px;
            height: 32px;
          }
        }
      }

      .single-attirbute {
        display: flex;
        align-items: center;
        gap: 12px;
        .number-input {
          width: 108px;
          height: 32px;
          ::v-deep(.round-button) {
            width: 32px;
            height: 32px;
          }
        }
        .stock-count {
          color: #B2B7C2;
          font-size: 11px;
          font-family: mon-500;
        }
      }
    }
  }
}
</style>
